// src/pages/InvoiceList.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllInvoices, getAllCustomers, getAllInvoiceItems, getAllStudents } from "../firebase/database";

export default function InvoiceList() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invoicesData] = await Promise.all([getAllInvoices()]);
      setInvoices(invoicesData);
    } catch (err) {
      console.error("Error loading invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN");
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
        <div style={{ color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto", padding: "24px" }}>
      <style>{`
        .invoice-header { display: flex; justify-content: space-between; align-items: center; marginBottom: 24px; gap: 16px; }
        .invoice-table-container { background: white; border-radius: 12px; boxShadow: 0 1px 3px rgba(0,0,0,0.1); overflow: hidden; }
        .invoice-mobile-cards { display: none; flex-direction: column; gap: 12px; }
        .invoice-card { background: white; border: 1px solid #E5E7EB; border-radius: 12px; padding: 16px; display: flex; flex-direction: column; gap: 12px; boxShadow: 0 1px 3px rgba(0,0,0,0.1); }
        .invoice-card-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #F3F4F6; padding-bottom: 10px; }
        .invoice-card-info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 13px; }
        .info-label { color: #6B7280; font-size: 11px; font-weight: 600; text-transform: uppercase; }
        .invoice-card-actions { display: flex; gap: 8px; flex-wrap: wrap; padding-top: 10px; border-top: 1px solid #F3F4F6; }

        @media (max-width: 768px) {
          .invoice-header { flex-direction: column; align-items: flex-start; }
          .invoice-header > a { width: 100%; text-align: center; }
          .invoice-table-container { display: none; }
          .invoice-mobile-cards { display: flex; }
        }
      `}</style>

      {/* Header */}
      <div className="invoice-header" style={{ marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <Link 
              to="/admin" 
              style={{ color: "#6B7280", textDecoration: "none", display: "flex", alignItems: "center" }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
              Invoices
            </h1>
          </div>
          <p style={{ color: "#6B7280", margin: "4px 0 0 44px", fontFamily: "DM Sans, sans-serif" }}>
            Manage customer invoices
          </p>
        </div>
        <Link
          to="/admin/invoices/create"
          style={{
            padding: "12px 24px",
            background: "#3B82F6",
            color: "white",
            border: "none",
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "DM Sans, sans-serif",
            textDecoration: "none",
            cursor: "pointer",
          }}
        >
          + Create Invoice
        </Link>
      </div>

      {/* Table */}
      <div className="invoice-table-container">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>INVOICE NO</th>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>DATE</th>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>CUSTOMER</th>
              <th style={{ padding: "14px 20px", textAlign: "right", fontSize: 12, fontWeight: 600, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>AMOUNT</th>
              <th style={{ padding: "14px 20px", textAlign: "center", fontSize: 12, fontWeight: 600, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>STATUS</th>
              <th style={{ padding: "14px 20px", textAlign: "center", fontSize: 12, fontWeight: 600, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 40, textAlign: "center", color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>
                  No invoices yet. Click "Create Invoice" to add one.
                </td>
              </tr>
            ) : (
              invoices.map((invoice, index) => (
                <tr key={invoice.id} style={{ borderBottom: index < invoices.length - 1 ? "1px solid #E5E7EB" : "none" }}>
                  <td style={{ padding: "14px 20px", fontWeight: 600, color: "#1F2937", fontFamily: "DM Sans, sans-serif" }}>
                    {invoice.invoiceNo}
                  </td>
                  <td style={{ padding: "14px 20px", color: "#374151", fontFamily: "DM Sans, sans-serif" }}>
                    {formatDate(invoice.date)}
                  </td>
                  <td style={{ padding: "14px 20px", color: "#374151", fontFamily: "DM Sans, sans-serif" }}>
                    {invoice.customerName}
                    {invoice.studentId && <span style={{ color: "#6B7280", fontSize: 12 }}> (ID: {invoice.studentId})</span>}
                  </td>
                  <td style={{ padding: "14px 20px", textAlign: "right", fontWeight: 600, color: "#1F2937", fontFamily: "DM Sans, sans-serif" }}>
                    {formatCurrency(invoice.totalAmount)}
                  </td>
                  <td style={{ padding: "14px 20px", textAlign: "center" }}>
                    <span style={{
                      padding: "4px 12px",
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: "DM Sans, sans-serif",
                      background: invoice.status === "paid" ? "#D1FAE5" : "#FEE2E2",
                      color: invoice.status === "paid" ? "#047857" : "#DC2626",
                    }}>
                      {invoice.status === "paid" ? "Paid" : "Pending"}
                    </span>
                  </td>
                  <td style={{ padding: "14px 20px", textAlign: "center" }}>
                    <Link
                      to={`/admin/invoices/${invoice.id}`}
                      style={{ padding: "6px 12px", fontSize: 12, background: "#E5E7EB", color: "#374151", borderRadius: 6, textDecoration: "none", fontFamily: "DM Sans, sans-serif" }}
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="invoice-mobile-cards">
        {invoices.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6B7280", fontFamily: "DM Sans, sans-serif", background: "white", borderRadius: 12 }}>
            No invoices yet.
          </div>
        ) : (
          invoices.map((invoice) => (
            <div key={invoice.id} className="invoice-card">
              <div className="invoice-card-header">
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1F2937" }}>{invoice.invoiceNo}</div>
                  <div style={{ fontSize: 12, color: "#6B7280" }}>{formatDate(invoice.date)}</div>
                </div>
                <span style={{
                  padding: "4px 10px",
                  borderRadius: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: "DM Sans, sans-serif",
                  background: invoice.status === "paid" ? "#D1FAE5" : "#FEE2E2",
                  color: invoice.status === "paid" ? "#047857" : "#DC2626",
                }}>
                  {(invoice.status || "pending").toUpperCase()}
                </span>
              </div>
              <div className="invoice-card-info">
                <div style={{ gridColumn: "span 2" }}>
                  <div className="info-label">Customer</div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{invoice.customerName}</div>
                  {invoice.studentId && <div style={{ color: "#6B7280", fontSize: 12 }}>ID: {invoice.studentId}</div>}
                </div>
                <div>
                  <div className="info-label">Total Amount</div>
                  <div style={{ fontWeight: 700, color: "#1F2937", fontSize: 16 }}>{formatCurrency(invoice.totalAmount)}</div>
                </div>
              </div>
              <div className="invoice-card-actions">
                <Link
                  to={`/admin/invoices/${invoice.id}`}
                  style={{ flex: 1, padding: "10px 12px", fontSize: 13, background: "#E5E7EB", color: "#374151", borderRadius: 8, textDecoration: "none", fontFamily: "DM Sans, sans-serif", fontWeight: 600, textAlign: "center" }}
                >
                  View Invoice Details
                </Link>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}